import { computed } from 'vue';
import { useStore } from 'vuex';
import type { ActingEntityType, ActingOrganization } from '@/store/modules/actingEntity';

/**
 * The entity the web-app user is acting as (themselves or an organization they admin), read from
 * the `actingEntity` store. Org-aware calls use `orgId` (undefined as the user), or
 * `entityType`/`entityId` for operations that take `entity` + `entityId`.
 */
export function useActingEntity() {
  const store = useStore();

  const organization = computed<ActingOrganization | null>(
    () => store.getters['actingEntity/organization'],
  );
  const organizations = computed<ActingOrganization[]>(
    () => store.getters['actingEntity/organizations'],
  );
  const isOrganization = computed<boolean>(() => store.getters['actingEntity/isOrganization']);
  const orgId = computed<string | undefined>(() => store.getters['actingEntity/orgId']);
  const entityType = computed<ActingEntityType>(() => store.getters['actingEntity/entityType']);
  const entityId = computed<string | undefined>(() => store.getters['actingEntity/entityId']);

  const selectOrganization = (org: ActingOrganization | null) => (
    store.dispatch('actingEntity/selectOrganization', org)
  );
  const loadOrganizations = () => store.dispatch('actingEntity/loadOrganizations');

  return {
    organization,
    organizations,
    isOrganization,
    orgId,
    entityType,
    entityId,
    selectOrganization,
    loadOrganizations,
  };
}

/** Profile page of whoever the user is acting as. */
export function actingProfilePath(
  organization: ActingOrganization | null,
  me: { id?: string; slug?: string } | null,
): string {
  if (organization) return `/web/profile/organization/${organization.id}`;
  if (me?.slug) return `/web/user/${me.slug}`;
  return me?.id ? `/web/profile/user/${me.id}` : '/web/profile';
}

/** Label under an organization's name, from `organization_type`. */
export function organizationKind(org: ActingOrganization | null): string {
  if (org?.organization_type === 'FORPROFIT') return 'For-profit organization';
  if (org?.organization_type === 'NONPROFIT') return 'Nonprofit organization';
  return 'Organization';
}
